import React, { useState, useEffect } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../services/firebaseConfig";
import { AiOutlineEyeInvisible, AiOutlineEye } from "react-icons/ai";
import Button from "../../../common/components/Button/Button";
import { useForm } from "react-hook-form";
import BeatLoader from "react-spinners/BeatLoader";
import {
  cryptFunction,
  decryptFunction,
} from "../../../common/Helper/crypt_decryptFunction";
import { detectBrowser } from "../../../common/Helper/checkCondition";
import axios from "axios";
import { useDispatch } from "react-redux";
import {
  getLoginSuccessAction,
  getLoginUserName,
} from "../../../redux/slices/loginSlice";
import { useNavigate } from "react-router-dom";
import { notify } from "../../../common/Helper/notification";

export const signInWithEmail = async (
  email,
  password,
  setErrorMessage,
  setLoading
) => {
  if (!email || !password) {
    console.error("Provide Email and Password");
    return;
  }
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;
    return user;
  } catch (error) {
    const errorMessages = {
      "auth/user-not-found": "The email address does not exist.",
      "auth/wrong-password": "Incorrect password. Please try again.",
      "auth/invalid-login-credentials":
        "Invalid login credentials. Please double-check your email and password.",
      "auth/invalid-credential":
        "Invalid credentials. Please enter valid email or password.",
      unknown: "An unknown error occurred.",
    };
    const errorMessage = errorMessages[error?.code ?? "unknown"];
    setErrorMessage(errorMessage);
    setLoading(false);
  }
};

const SignIn = ({
  clickHandler,
  userDetailHandlerSignIn,
  switchLogin,
  switchForget,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    handleSubmit,
    register,
    trigger,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch("password");

  // Capture the Enter key to submit the form
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSubmit(onSubmit)(); // Programmatically trigger form submit
    }
  };

  useEffect(() => {
    if (errorMessage && String(errorMessage).length > 0)
      notify.error(errorMessage);
  }, [errorMessage]);

  const onSubmit = async (data) => {
    localStorage.removeItem("rowsPerPage");
    setLoading(true);

    if (!navigator.onLine) {
      setLoading(false);
      setErrorMessage(
        "No internet connection. Please check your connection and try again."
      );
      return;
    }

    if (rememberMe) {
      const token = {
        rememberMe: rememberMe,
        savedUsername: data.username,
        savedPassword: data.password,
      };
      let cryptToken = cryptFunction(token);
      localStorage.setItem("token", JSON.stringify(cryptToken));
    } else {
      localStorage.removeItem("token");
    }

    try {
      const userDetails = await signInWithEmail(
        data.username,
        data.password,
        setErrorMessage,
        setLoading
      );

      localStorage.setItem("accessToken", userDetails?.accessToken);

      const email = data.username;
      if (userDetails && email) {
        axios
  .get(`${process.env.REACT_APP_SIGNIN_CRUD_API}&email=${email}`)
  .then((response) => {

    if (response?.data.datafound) {
      // Dispatch only — do NOT call navigate("/landing") here.
      //
      // The previous `dispatch(...); navigate("/landing")` sequence raced
      // against React 18's batched render: the URL sometimes changed before
      // <Routing> re-rendered with the new isAuthenticated, leaving the user
      // briefly in PublicRoutes-at-/landing → its `*` catch-all bounced them
      // back to /, where they appeared stuck on the login form until they
      // refreshed (which bootstraps cleanly from localStorage).
      //
      // The dispatch flips isAuthenticated to true and writes authToken to
      // localStorage synchronously. On the next render, <Routing> swaps to
      // <ProtectedRoutes>, whose `*` catch-all redirects to /landing. No race.
      dispatch(getLoginSuccessAction(response?.data));
      dispatch(getLoginUserName(response.data.data[0].FirstName));
    }

  })
  .catch((error) => {
    setErrorMessage(error?.message ?? "Something went wrong");
  });
      }
    } catch (err) {
      setErrorMessage("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetErrorMessage = () => {
    setErrorMessage("");
  };

  const togglePasswordVisibility = (e) => {
    e.preventDefault(); // Prevent default behavior (like text selection)
    e.stopPropagation();
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  useEffect(() => {
    let decryptToken = JSON.parse(localStorage.getItem("Token"));
    if (decryptToken) {
      let token = decryptFunction(decryptToken);
      const isRememberMeChecked = token.rememberMe;
      const savedUsername = token.savedUsername;
      const savedPassword = token.savedPassword;
      if (savedUsername && savedPassword) {
        setRememberMe(isRememberMeChecked);
        setValue("username", savedUsername);
        setValue("password", savedPassword);
        setValue("rememberMe", isRememberMeChecked);
        trigger();
      }
    }
  }, [setValue, trigger]);

  const detectedBrowser = detectBrowser();

  return (
    <div className="login-inner-container">
      <div className="login-sign-in-container">
        <p className="heading-text text-center">Welcome Back!</p>
        <form
          className="w-100"
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={handleKeyDown} // Listen to the keypress
        >
          <div className="login-input-container">
            <div className="input-groups">
              <div className="username-container">
                <label className="login-label-text" htmlFor="inputusername">
                  Username<span className="requiredAstericSign">*</span>
                </label>
                <input
                  className="login-input"
                  id="inputusername"
                  type="text"
                  {...register("username", {
                    required: "Username is required",
                    validate: {
                      email: (value) =>
                        /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
                          value
                        ) || "Invalid email address.",
                    },
                  })}
                  onChange={(e) => {
                    setValue("username", e.target.value);
                    resetErrorMessage();
                  }}
                  placeholder="Tell us your email address"
                />
                <span style={{ color: "red" }}>
                  {errors.username && errors.username.message}
                </span>
              </div>

              <div className="login-password">
                <label className="login-label-text" htmlFor="inputPassword">
                  Password<span className="requiredAstericSign">*</span>
                </label>
                <input
                  className="login-input"
                  type={showPassword ? "text" : "password"}
                  id="inputPassword"
                  {...register("password", {
                    required: "Password is required",
                    validate: {
                      atLeastOneUppercase: (value) =>
                        /[A-Z]/.test(value) ||
                        "It must contain at least one uppercase letter.",
                      atLeastOneDigit: (value) =>
                        /\d/.test(value) ||
                        "It must contain at least one digit (numeric value).",
                      minLength: (value) =>
                        value.length >= 8 ||
                        "It must be at least 8 characters long.",
                      atLeastOneSpecialSymbol: (value) =>
                        /[^A-Za-z\d]+/.test(value) ||
                        "It must contain at least one special symbol.",
                    },
                  })}
                  onChange={(e) => {
                    setValue("password", e.target.value);
                    resetErrorMessage();
                  }}
                  style={{ padding: detectedBrowser === "Safari" ? 5 : 0 }}
                  placeholder="Enter your password"
                />
                <span style={{ color: "red", display: "block", marginTop: 3 }}>
                  {errors.password && errors.password.message}
                  {errorMessage && (
                    <div className="error-message">{errorMessage}</div>
                  )}
                </span>
                <span
                  onClick={togglePasswordVisibility}
                  className="login-password-eye"
                  style={{ top: detectedBrowser === "Safari" ? 38 : 30 }}
                >
                  {password && password.length > 0 ? (
                    showPassword ? (
                      <AiOutlineEye size={21} />
                    ) : (
                      <AiOutlineEyeInvisible size={21} />
                    )
                  ) : null}
                </span>
                <a
                  href="/#"
                  className="forget-text"
                  onClick={(e) => {
                    e.preventDefault();
                    switchForget(e.target);
                  }}
                >
                  Forgot password?
                </a>
              </div>

              <div className="input-remember">
                <input
                  type="checkbox"
                  id="inputCheckbox"
                  {...register("rememberMe")}
                  onChange={() => setRememberMe(!rememberMe)}
                />
                <label
                  className="login-label-text-remember"
                  htmlFor="inputCheckbox"
                >
                  Remember Me
                </label>
              </div>

              <Button
                type="submit"
                className="primary login-button"
                disabled={loading} // Disable button when loading
              >
                {loading ? (
                  <BeatLoader color={"white"} loading={loading} size={10} />
                ) : (
                  "Sign In"
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignIn;
